import posthog from "posthog-js";
import { silenceKnownConsoleNoise } from "./silenceKnownConsoleNoise";

// Project API keys are public by design — they are shipped inside the client
// bundle and can only write events. Env vars still take precedence so a fork or
// a staging build can point at a different project without a code change.
const POSTHOG_KEY =
  process.env.REACT_APP_PUBLIC_POSTHOG_KEY ||
  "phc_uy2Ws8rtdKMo6Uwv43Pv6S73TdNjSdCuLRuJpxLSe3C8";

const POSTHOG_HOST =
  process.env.REACT_APP_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

const ENVIRONMENT =
  process.env.REACT_APP_POSTHOG_ENVIRONMENT || process.env.NODE_ENV;

let ready = false;

export const isPostHogReady = () => ready;

/**
 * Noise we never want in Error Tracking: third-party CDNs, React/antd
 * deprecation warnings that leak as exception text, and browser extension
 * frames. Prefer fixing sources; this is the safety net for leftovers.
 */
const IGNORED_EXCEPTION_PATTERNS = [
  /weatherwidget\.org/i,
  /ww_275a90dd62a66/i,
  /Cannot read properties of null \(reading 'getAttribute'\)/i,
  /findDOMNode is deprecated/i,
  /forwardRef render functions accept exactly two parameters/i,
  /Each child in a list should have a unique ["']key["'] prop/i,
  /\[antd:/i,
  /ResizeObserver loop/i,
  /Script error\.?/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,
];

function exceptionText(event) {
  const list = event?.properties?.$exception_list || [];
  const parts = [];
  for (const item of list) {
    if (item?.type) parts.push(String(item.type));
    if (item?.value) parts.push(String(item.value));
    const frames = item?.stacktrace?.frames || [];
    for (const frame of frames) {
      if (frame?.filename) parts.push(String(frame.filename));
      if (frame?.source) parts.push(String(frame.source));
    }
  }
  if (event?.properties?.$exception_message) {
    parts.push(String(event.properties.$exception_message));
  }
  if (event?.properties?.$exception_source) {
    parts.push(String(event.properties.$exception_source));
  }
  return parts.join("\n");
}

export const shouldDropPostHogEvent = (event) => {
  if (!event || event.event !== "$exception") return false;
  const text = exceptionText(event);
  return IGNORED_EXCEPTION_PATTERNS.some((re) => re.test(text));
};

export const initPostHog = () => {
  if (ready || !POSTHOG_KEY) return;

  silenceKnownConsoleNoise();

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: "https://us.posthog.com",

    // Route changes are captured by PathTracker so that a single React Router
    // navigation produces exactly one $pageview.
    capture_pageview: false,
    capture_pageleave: true,

    // Everyone using FlexHR is behind Cognito, so anonymous profiles on the
    // login screen would only add cost without adding signal.
    person_profiles: "identified_only",

    // Unhandled errors/rejections yes; console.error noise no.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },

    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      // rrweb masks password inputs by default even when maskAllInputs is off.
      maskInputOptions: { password: false },
    },

    before_send: (event) => {
      if (shouldDropPostHogEvent(event)) return null;
      return event;
    },

    loaded: (ph) => {
      ph.register({ environment: ENVIRONMENT });
      if (process.env.NODE_ENV !== "production") ph.debug();
    },
  });

  ready = true;
};

export default posthog;
